import { createSlice } from '@reduxjs/toolkit';
import type { IataCode } from '../../services/api/iataCodeService';
import {
  fetchIataCodes, fetchActiveIataCodes,
  createIataCode, updateIataCode, deleteIataCode,
  activateIataCode, deactivateIataCode,
} from '../thunks/iataCodeThunks';

export interface IataCodeState {
  iataCodes: IataCode[];
  activeIataCodes: IataCode[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: IataCodeState = {
  iataCodes: [],
  activeIataCodes: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
};

const iataCodeSlice = createSlice({
  name: 'iataCode',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // --- Fetch IATA Codes (admin, paginated) ---
    builder
      .addCase(fetchIataCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIataCodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.iataCodes = action.payload.iataCodes;
        state.totalCount = action.payload.totalCount;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchIataCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Fetch Active IATA Codes (dropdown) ---
    builder
      .addCase(fetchActiveIataCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveIataCodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeIataCodes = action.payload;
      })
      .addCase(fetchActiveIataCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Create IATA Code ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(createIataCode.pending, (state) => { state.isLoading = true; })
      .addCase(createIataCode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createIataCode.rejected, (state) => { state.isLoading = false; });

    // --- Update IATA Code ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(updateIataCode.pending, (state) => { state.isLoading = true; })
      .addCase(updateIataCode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateIataCode.rejected, (state) => { state.isLoading = false; });

    // --- Delete IATA Code ---
    builder
      .addCase(deleteIataCode.fulfilled, (state, action) => {
        state.iataCodes = state.iataCodes.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });

    // --- Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateIataCode.pending, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateIataCode.fulfilled, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.payload);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateIataCode.rejected, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateIataCode.pending, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateIataCode.fulfilled, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.payload);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateIataCode.rejected, (state, action) => {
        const code = state.iataCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      });
  },
});

export const { clearError } = iataCodeSlice.actions;
export default iataCodeSlice.reducer;
